import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../api/client";
import useAuth from "../hooks/useAuth";
import useIsMobile from "../hooks/useIsMobile";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";

function getMonday(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  return dt;
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
}

function formatShortDate(iso) {
  const d = new Date(iso + "T00:00:00");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function formatFullDate(iso) {
  const d = new Date(iso + "T00:00:00");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

export default function FSTDailyOutputPage() {
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const today = useMemo(() => new Date(), []);
  const currentMonday = useMemo(() => getMonday(today), [today]);

  const [weekStart, setWeekStart] = useState(currentMonday);
  const [customRange, setCustomRange] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const startDate = customRange ? fromDate : formatDate(weekStart);
  const endDate = customRange ? toDate : formatDate(addDays(weekStart, 6));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const { data: resp } = await api.get("/reports/fst-daily-output", {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(resp);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const prevWeek = () => {
    setCustomRange(false);
    setWeekStart(addDays(weekStart, -7));
  };
  const nextWeek = () => {
    setCustomRange(false);
    setWeekStart(addDays(weekStart, 7));
  };
  const currentWeek = () => {
    setCustomRange(false);
    setWeekStart(currentMonday);
  };
  const applyRange = () => {
    if (fromDate && toDate) {
      setCustomRange(true);
    }
  };
  const resetRange = () => {
    setCustomRange(false);
    setFromDate("");
    setToDate("");
    setWeekStart(currentMonday);
  };

  const weekNumber = data?.week_number ?? "";
  const headerRange = data
    ? `${formatFullDate(data.start_date)} - ${formatFullDate(data.end_date)}`
    : "";

  const scoreClass = (s) =>
    s > 0 ? "text-blue-700" : s < 0 ? "text-red-600" : "text-gray-400";

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <div className="flex-1 px-3 py-4 max-w-lg mx-auto w-full sm:px-4">
        {/* Title */}
        <div className="mb-4">
          <h1 className="text-lg font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-500">Daily Output &mdash; {user?.display_name || user?.full_name}</p>
        </div>

        {/* Week header */}
        {!customRange && (
          <div className="text-center mb-3">
            <p className="text-sm font-semibold text-blue-700">Week {weekNumber}</p>
            <p className="text-xs text-gray-500">{headerRange}</p>
          </div>
        )}
        {customRange && (
          <div className="text-center mb-3">
            <p className="text-sm font-semibold text-blue-700">Custom Range</p>
            <p className="text-xs text-gray-500">{headerRange}</p>
          </div>
        )}

        {/* Week navigation */}
        {!customRange && (
          <div className="flex items-center justify-between gap-2 mb-3">
            <button onClick={prevWeek} className="flex-1 px-2 py-1.5 text-xs font-medium rounded bg-gray-200 active:bg-gray-300 text-gray-700">
              &larr; Prev
            </button>
            <button onClick={currentWeek} className="flex-1 px-2 py-1.5 text-xs font-medium rounded bg-blue-600 text-white active:bg-blue-700">
              Current
            </button>
            <button onClick={nextWeek} className="flex-1 px-2 py-1.5 text-xs font-medium rounded bg-gray-200 active:bg-gray-300 text-gray-700">
              Next &rarr;
            </button>
          </div>
        )}

        {/* Date range selector */}
        <details className="mb-4 bg-white rounded-lg shadow-sm border">
          <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer select-none">
            Custom Date Range
          </summary>
          <div className="px-3 pb-3 pt-1 space-y-2">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-0.5">From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-0.5">To</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={applyRange}
                disabled={!fromDate || !toDate}
                className="flex-1 px-3 py-1.5 text-xs font-medium rounded bg-blue-600 text-white active:bg-blue-700 disabled:opacity-50"
              >
                Apply
              </button>
              <button
                onClick={resetRange}
                className="flex-1 px-3 py-1.5 text-xs font-medium rounded bg-gray-200 text-gray-700 active:bg-gray-300"
              >
                Reset to Current Week
              </button>
            </div>
          </div>
        </details>

        {/* Loading / Error */}
        {loading && (
          <div className="text-center py-8 text-sm text-gray-400">Loading...</div>
        )}
        {error && (
          <div className="text-center py-4 text-sm text-red-600 bg-red-50 rounded-lg">{error}</div>
        )}

        {/* Data table */}
        {data && !loading && (
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-700 text-white text-xs">
                  <th className="px-2 py-2 text-left font-medium">Day</th>
                  <th className="px-2 py-2 text-left font-medium">Date</th>
                  <th className="px-2 py-2 text-center font-medium">Done</th>
                  <th className="px-2 py-2 text-center font-medium">Cancel</th>
                  <th className="px-2 py-2 text-center font-medium">Resched</th>
                  <th className="px-2 py-2 text-center font-medium" title="Attachment Compliance — manual deduction">Att</th>
                  <th className="px-2 py-2 text-center font-medium">Score</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => {
                  const isToday = row.date === formatDate(today);
                  return (
                    <tr
                      key={row.date}
                      className={`border-t ${isToday ? "bg-blue-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                    >
                      <td className={`px-2 py-2 text-xs ${isToday ? "font-bold text-blue-700" : "text-gray-600"}`}>{row.day}</td>
                      <td className={`px-2 py-2 text-xs ${isToday ? "font-bold text-blue-700" : "text-gray-700"}`}>{formatShortDate(row.date)}</td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-block min-w-[24px] text-xs font-semibold ${row.completed > 0 ? "text-green-700" : "text-gray-400"}`}>
                          {row.completed}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-block min-w-[24px] text-xs font-semibold ${row.cancelled > 0 ? "text-red-600" : "text-gray-400"}`}>
                          {row.cancelled}
                          {row.cancelled_raw != null && row.cancelled_raw !== row.cancelled && (
                            <span className="text-[10px] text-gray-400 ml-0.5">({row.cancelled_raw})</span>
                          )}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-block min-w-[24px] text-xs font-semibold ${row.rescheduled > 0 ? "text-amber-600" : "text-gray-400"}`}>
                          {row.rescheduled}
                          {row.rescheduled_raw != null && row.rescheduled_raw !== row.rescheduled && (
                            <span className="text-[10px] text-gray-400 ml-0.5">({row.rescheduled_raw})</span>
                          )}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-center">
                        <AttCellMobile
                          value={row.att ?? 0}
                          userId={data.user_id}
                          date={row.date}
                          canEdit={!!data.can_edit_att}
                          onSaved={fetchData}
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className={`inline-block min-w-[24px] text-xs font-bold ${scoreClass(row.score)}`}>
                          {row.score}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-blue-200 bg-blue-50">
                  <td className="px-2 py-2 text-xs font-bold text-gray-800" colSpan={2}>Total</td>
                  <td className="px-2 py-2 text-center text-xs font-bold text-green-700">{data.total.completed}</td>
                  <td className="px-2 py-2 text-center text-xs font-bold text-red-600">
                    {data.total.cancelled}
                    {data.total.cancelled_raw != null && data.total.cancelled_raw !== data.total.cancelled && (
                      <span className="text-[10px] text-gray-400 ml-0.5">({data.total.cancelled_raw})</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-center text-xs font-bold text-amber-600">
                    {data.total.rescheduled}
                    {data.total.rescheduled_raw != null && data.total.rescheduled_raw !== data.total.rescheduled && (
                      <span className="text-[10px] text-gray-400 ml-0.5">({data.total.rescheduled_raw})</span>
                    )}
                  </td>
                  <td className={`px-2 py-2 text-center text-xs font-bold ${(data.total.att ?? 0) > 0 ? "text-red-600" : "text-gray-400"}`}>{data.total.att ?? 0}</td>
                  <td className={`px-2 py-2 text-center text-xs font-bold ${scoreClass(data.total.score)}`}>{data.total.score}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AttCellMobile({ value, userId, date, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(String(value ?? 0));
  }, [value]);

  if (!canEdit || !userId) {
    return (
      <span className={`inline-block min-w-[24px] text-xs font-semibold ${value > 0 ? "text-red-600" : "text-gray-400"}`}>
        {value}
      </span>
    );
  }

  const commit = async () => {
    const n = Math.max(0, parseInt(draft, 10) || 0);
    setEditing(false);
    if (n === value) return;
    setSaving(true);
    try {
      await api.put("/reports/attachment-score", {
        user_id: userId,
        date,
        value: n,
      });
      onSaved && onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save Att");
      setDraft(String(value ?? 0));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        type="number"
        min="0"
        autoFocus
        disabled={saving}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") {
            setDraft(String(value ?? 0));
            setEditing(false);
          }
        }}
        className="w-12 px-1 py-0.5 text-xs text-center border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`inline-block min-w-[24px] px-1 py-0.5 text-xs font-semibold rounded active:bg-blue-100 ${value > 0 ? "text-red-600" : "text-gray-400"}`}
    >
      {value}
    </button>
  );
}
